export type ProjectVisibility = 'detail' | 'card';
export type ProjectTier = 'client-proof' | 'lab';
export type ProofStyle = 'operating-layer' | 'governance' | 'traceability' | 'data-insight' | 'outcome' | 'future-signal';

export type Project = {
  id: string;
  title: string;
  kicker: string;
  summary: string;
  proofStyle: ProofStyle;
  visibility: ProjectVisibility;
  tier: ProjectTier;
  image: string;
  imageAlt: string;
  safeDisclosure: string;
  highlights: string[];
  detail: {
    headline: string;
    thesis: string;
    sections: { title: string; body: string }[];
    outcomes: string[];
    clientRelevance: string;
  };
};

export const projects: Project[] = [
  {
    id: 'colby',
    title: 'Colby',
    kicker: 'AI workbench',
    summary:
      'A personal AI workbench for knowledge, decisions, tasks, automation, and follow-through, with human approval where it matters.',
    proofStyle: 'operating-layer',
    visibility: 'detail',
    tier: 'client-proof',
    image: '/assets/colby-reference.png',
    imageAlt: 'Colby visual identity in a field operations setting',
    safeDisclosure:
      'Public detail stays at the operating-pattern level. Private memory, keys, personal systems, and sensitive automations stay out of view.',
    highlights: ['Decision support', 'Task orchestration', 'Knowledge operations', 'Human approval'],
    detail: {
      headline: 'A practical operating layer for turning intent into follow-through.',
      thesis:
        'Colby is the working expression of a larger belief: AI is most valuable when it is connected to memory, standards, tasks, review, and human authority. It is not meant to be a chatbot novelty. It is a way to keep complex work moving without losing accountability.',
      sections: [
        {
          title: 'The problem',
          body:
            'Most AI use stalls after the impressive answer. The work still has to be organized, reviewed, remembered, assigned, checked, and improved. Colby is built around that messy middle: the space between an idea and an operational result.'
        },
        {
          title: 'The operating model',
          body:
            'The system keeps context, drafts responses, tracks leads and tasks, preserves decisions, and routes sensitive actions through review. The point is not autonomy for its own sake. The point is better judgment at higher tempo.'
        },
        {
          title: 'The lesson',
          body:
            'Useful AI needs boundaries, memory, workflow, and evidence. Colby gives Greg a live environment for testing those patterns before recommending them to clients.'
        }
      ],
      outcomes: ['Working task and lead flow', 'Human-reviewed responses', 'Knowledge reuse', 'Governed automation posture'],
      clientRelevance:
        'For clients, Colby demonstrates how private AI, knowledge operations, and workflow automation can become a governed operating capability instead of a pile of disconnected tools.'
    }
  },
  {
    id: 'software-factory',
    title: 'Software Factory',
    kicker: 'Governed delivery',
    summary:
      'A governed way to move from idea to working software with requirements, approvals, tests, reviews, and evidence.',
    proofStyle: 'governance',
    visibility: 'detail',
    tier: 'client-proof',
    image: '/assets/project-software-factory.png?v=20260927',
    imageAlt: 'Software Factory command center showing governed delivery flow from ideas and data through requirements, standards, build, test, deploy, and operations',
    safeDisclosure:
      'Public detail focuses on the delivery model and governance pattern without exposing internals, private run state, internal controls, or sensitive implementation details.',
    highlights: ['Discovery', 'Requirements', 'Governed execution', 'Evidence-first review'],
    detail: {
      headline: 'AI can accelerate the build. The hard part is making delivery dependable.',
      thesis:
        'Software Factory is the answer to a problem Greg talks about often: writing software with AI is becoming easier, but knowing what to build, proving it works, controlling risk, and preserving decisions are where the value is moving.',
      sections: [
        {
          title: 'The problem',
          body:
            'AI coding tools can produce output quickly. That speed becomes a liability when requirements are unclear, approvals are informal, evidence is thin, and no one can explain why a change was made.'
        },
        {
          title: 'The system',
          body:
            'The factory treats software delivery as an operating system: intake, discovery, requirements, architecture, build, tests, review, evidence, approval, and release. The goal is not bureaucracy. The goal is speed that can survive scrutiny.'
        },
        {
          title: 'The discipline',
          body:
            'Every run has a known scope, authority level, evidence trail, and release gate. That structure lets AI help with the work without becoming the authority for the work.'
        }
      ],
      outcomes: ['Requirements-to-build traceability', 'Approval-gated delivery', 'Evidence packets', 'Repeatable AI-enabled execution'],
      clientRelevance:
        'This is the pattern behind AI delivery systems Greg can help design: faster execution with clearer authority, stronger review, and less drift between leadership intent and working software.'
    }
  },
  {
    id: 'argus',
    title: 'ARGUS',
    kicker: 'Defensible reasoning',
    summary:
      'ARGUS, the Agentic Reasoning and Governed Unified System, is a reasoning framework for source-linked AI-assisted decisions with human accountability.',
    proofStyle: 'traceability',
    visibility: 'detail',
    tier: 'client-proof',
    image: '/assets/project-argus.png?v=20260927',
    imageAlt: 'ARGUS mission reasoning display showing mission inputs, bounded AI agents, a claim-evidence graph, human review, and a decision product',
    safeDisclosure:
      'Public detail stays at the architecture and principle level until deeper implementation disclosures are approved.',
    highlights: ['Explainability', 'Human governance', 'Decision traceability', 'Trust boundaries'],
    detail: {
      headline: 'Consequential AI has to explain itself.',
      thesis:
        'ARGUS is about the difference between an answer and a defensible decision. In serious environments, AI has to show its sources, expose uncertainty, respect authority boundaries, and leave a trail a human can inspect.',
      sections: [
        {
          title: 'The problem',
          body:
            'Many AI systems produce fluent recommendations without showing enough evidence to trust them. That may be tolerable for low-risk work. It is not enough for operational, financial, legal, mission, or executive decisions.'
        },
        {
          title: 'The framework',
          body:
            'ARGUS frames AI reasoning around source grounding, stated assumptions, confidence, dissenting evidence, approval boundaries, and traceable human decisions.'
        },
        {
          title: 'The standard',
          body:
            'The goal is not to make AI sound certain. The goal is to make the uncertainty visible enough for responsible people to act with judgment.'
        }
      ],
      outcomes: ['Source-aware reasoning', 'Uncertainty surfaced', 'Human decision checkpoints', 'Audit-friendly records'],
      clientRelevance:
        'For organizations adopting AI, ARGUS points toward the governance layer executives need before they rely on AI for decisions that carry real consequences.'
    }
  },
  {
    id: 'executive-advisor',
    title: 'Executive Advisor',
    kicker: 'AI executive support',
    summary:
      'An AI-first advisory system for small and medium business (SMB) owners and chief executive officers (CEOs) that preserves company context, challenges assumptions, and turns recommendations into follow-through.',
    proofStyle: 'operating-layer',
    visibility: 'detail',
    tier: 'client-proof',
    image: '/assets/project-executive-advisor.png?v=20260927',
    imageAlt: 'Executive Advisor dashboard showing business priorities, decisions, risks, finance, team workload, and action items',
    safeDisclosure:
      'Public detail describes the product pattern and approved requirements without exposing private customer data, internal implementation details, or confidential operating records.',
    highlights: ['Decision quality', 'Company memory', 'Role-aware work', 'Follow-through'],
    detail: {
      headline: 'A trusted executive partner, not a chatbot with memory.',
      thesis:
        'Executive Advisor is built for owners and CEOs who need broader executive thinking without hiring a full traditional C-suite. It is designed to understand the company over time, preserve context, challenge weak assumptions, and help leaders make better decisions with evidence and follow-through.',
      sections: [
        {
          title: 'The problem',
          body:
            'Small and medium business leaders often carry strategy, finance, operations, people, sales, technology, risk, and institutional memory in their own heads. That creates bottlenecks, missed second-order consequences, weak follow-through, and decisions made without enough evidence.'
        },
        {
          title: 'The advisor',
          body:
            'The system supports conversational executive advice, persistent company and role context, role-aware recommendations, daily brief behavior, conflict detection, provenance, and natural-language approval of numbered recommendations.'
        },
        {
          title: 'The standard',
          body:
            'The product promise is truth and decision quality over agreement. The advisor should surface uncertainty, distinguish facts from assumptions, recommend concrete next steps, and keep consequential execution under human control.'
        }
      ],
      outcomes: ['Persistent company context', 'Numbered recommendations', 'Role-aware delegation', 'Provenance and outcome tracking'],
      clientRelevance:
        'For clients, Executive Advisor shows the kind of AI capability Greg helps design: practical, governed, commercially useful, and connected to how leaders actually run the business.'
    }
  },
  {
    id: 'off-grid-systems-lab',
    title: 'Off-Grid Systems Lab',
    kicker: 'Resilient infrastructure',
    summary:
      'A practical systems lab for energy production, load behavior, automation, local intelligence, and resilient operations.',
    proofStyle: 'data-insight',
    visibility: 'detail',
    tier: 'lab',
    image: '/assets/project-off-grid-systems-lab.png',
    imageAlt: 'An off-grid energy equipment room with battery storage and a monitoring dashboard',
    safeDisclosure:
      'Public detail can share loads, production patterns, and lessons without personal information, private household detail, location detail, or security-sensitive configuration.',
    highlights: ['Load behavior', 'Production insight', 'Automation', 'Operational resilience'],
    detail: {
      headline: 'A living lab for data, automation, and resilience.',
      thesis:
        'The Off-Grid Systems Lab is not a lifestyle page. It is a practical way to study how real systems behave: production, storage, consumption, constraints, forecasts, automation, and human habits.',
      sections: [
        {
          title: 'The problem',
          body:
            'Reliable operations are built from details. Energy systems make that obvious because demand, weather, storage, failure modes, and human behavior all interact.'
        },
        {
          title: 'The experiment',
          body:
            'The lab creates a safe public way to discuss load profiles, production patterns, automation decisions, and local-first intelligence without exposing private personal information.'
        },
        {
          title: 'The lesson',
          body:
            'Good AI and automation depend on operational context. The more grounded the data, the better the decisions. The more visible the constraints, the more useful the system becomes.'
        }
      ],
      outcomes: ['Load and production analysis', 'Automation lessons', 'Local-first intelligence patterns', 'Resilience design insights'],
      clientRelevance:
        "For clients, this reinforces Greg's bias toward practical instrumentation: measure the system, understand the constraints, automate carefully, and keep improving."
    }
  },
  {
    id: 'rig',
    title: 'RV Intelligence Guide',
    kicker: 'Applied owner intelligence',
    summary:
      'A recreational vehicle (RV) intelligence workspace for records, diagnostics, maintenance context, and Colby-guided next steps.',
    proofStyle: 'data-insight',
    visibility: 'detail',
    tier: 'lab',
    image: '/assets/project-rig.png',
    imageAlt: 'RV Intelligence Guide dashboard concept for an Outlaw recreational vehicle with diagnostics, gauges, and recommended next steps',
    safeDisclosure:
      'Public detail can show the product pattern and sanitized vehicle concepts without exposing private owner records, account data, locations, or live diagnostic feeds.',
    highlights: ['Diagnostics context', 'Maintenance records', 'Guided next steps', 'Owner memory'],
    detail: {
      headline: 'A practical intelligence layer for the vehicle you actually own.',
      thesis:
        'RV Intelligence Guide (RIG) applies the same operating-system mindset to a physical asset: capture the details, preserve the history, connect symptoms to records, and turn scattered information into a next useful action.',
      sections: [
        {
          title: 'The problem',
          body:
            'RV ownership creates a lot of loose context: chassis details, coach systems, service records, trouble codes, add-ons, repair conversations, manuals, and notes from the last time something failed. The hard part is not collecting one more document. The hard part is making the whole record useful when a decision has to be made.'
        },
        {
          title: 'The system',
          body:
            'RIG is designed as an owner workspace where Colby can help organize symptoms, preserve maintenance history, prepare repair-shop packets, and suggest bounded next checks. The goal is a calmer path from “something is wrong” to “here is what we know, here is what to verify, and here is what to do next.”'
        },
        {
          title: 'The lesson',
          body:
            'This is a small-domain version of a larger enterprise pattern: useful AI needs grounded records, clear authority, traceable recommendations, and a human who stays in control of the final decision.'
        }
      ],
      outcomes: ['Owner-centered diagnostic workspace', 'Maintenance and document memory', 'Repair-shop handoff packets', 'Bounded AI guidance'],
      clientRelevance:
        'For clients, RIG shows how a focused AI product can turn messy operational context into usable workflow without pretending the AI is the mechanic, authority, or final decision maker.'
    }
  },
  {
    id: 'chi',
    title: 'Cognitive Home Interface (CHI)',
    kicker: 'Cognitive home interface',
    summary:
      'A developing concept for better alignment between people, routines, home systems, knowledge, and the interfaces that carry daily life forward.',
    proofStyle: 'future-signal',
    visibility: 'card',
    tier: 'lab',
    image: '/assets/project-chi.png?v=20260928-chi-home',
    imageAlt: 'Cognitive Home Interface scene showing a connected home, energy systems, vehicle status, and a CHI conversation panel',
    safeDisclosure:
      'Public detail remains high-level until Greg approves a deeper public narrative.',
    highlights: ['Concept', 'Home systems', 'Workflow alignment', 'Daily capability'],
    detail: {
      headline: 'The interface between human judgment and working systems.',
      thesis:
        'Cognitive Home Interface (CHI) is an emerging idea around a simple tension: homes do not just need more devices. They need better alignment between people, decisions, routines, knowledge, and the systems that carry daily life forward.',
      sections: [
        {
          title: 'The question',
          body:
            'As AI becomes easier to deploy, the limiting factor shifts toward human capability: who decides, who reviews, who learns, who owns the workflow, and how the system helps without taking over.'
        },
        {
          title: 'The direction',
          body:
            'CHI explores the design space between household intent, resident judgment, AI assistance, and operating rhythm. It is less about interface polish and more about making the home more understandable, useful, and resilient.'
        },
        {
          title: 'The posture',
          body:
            'This remains a concept area, intentionally lighter than Colby, Software Factory, ARGUS, or Executive Advisor.'
        }
      ],
      outcomes: ['Home-centered operating models', 'Decision workflow concepts', 'Capability design language', 'Future research direction'],
      clientRelevance:
        'CHI signals where Greg is looking next: AI adoption as a lived operating problem, not just a technology rollout.'
    }
  },
  {
    id: 'autonomous-mobility',
    title: 'Autonomous Mobility',
    kicker: 'Applied autonomy',
    summary:
      'Exploration around autonomy, rural operations, electrification, and the operating models future mobility will require.',
    proofStyle: 'future-signal',
    visibility: 'card',
    tier: 'lab',
    image: '/assets/project-autonomous-mobility.png?v=20260928-autonomous-shop',
    imageAlt: 'Autonomous Mobility scene showing a Cognitive Workstead shop with solar power, electric vehicles, a tractor, and rural operations equipment',
    safeDisclosure:
      'Public detail avoids implying affiliation, endorsement, or inside information.',
    highlights: ['Autonomy', 'Rural operations', 'Electrification', 'Applied innovation'],
    detail: {
      headline: 'Autonomy only matters when it fits real operations.',
      thesis:
        'Autonomous Mobility is an exploration of what happens when AI, electrification, rural operations, physical logistics, and human oversight meet outside a clean demo environment.',
      sections: [
        {
          title: 'The problem',
          body:
            'Mobility innovation is often discussed as a product story. The harder question is operational: where does it work, who owns it, how is it maintained, what data does it need, and what happens when conditions change?'
        },
        {
          title: 'The operating view',
          body:
            'The useful lens is not just vehicle autonomy. It is autonomy inside a larger system of energy, routing, maintenance, safety, human review, local context, and business value.'
        },
        {
          title: 'The status',
          body:
            'This is a future-facing lab concept, intentionally framed as exploration rather than a claim of implementation or affiliation.'
        }
      ],
      outcomes: ['Operating model questions', 'Rural autonomy scenarios', 'Electrification considerations', 'Applied innovation lens'],
      clientRelevance:
        'For clients, this shows how Greg evaluates emerging technology: start with real operating conditions, then decide what technology is actually useful.'
    }
  }
];
